import { forwardRef, useId } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/utils";
import type {
  ModalProps,
  ModalHeaderProps,
  ModalBodyProps,
  ModalFooterProps,
} from "./Modal.types";
import { useModalStack } from "./modal-stack";

const modalSizes: Record<NonNullable<ModalProps["size"]>, string> = {
  xs: "max-w-full md:max-w-xs",
  sm: "max-w-full md:max-w-sm",
  md: "max-w-full md:max-w-md",
  lg: "max-w-full md:max-w-lg",
  xl: "max-w-full md:max-w-xl",
  "2xl": "max-w-full md:max-w-2xl",
  "3xl": "max-w-full md:max-w-3xl",
  "4xl": "max-w-full md:max-w-4xl",
  "5xl": "max-w-full md:max-w-5xl",
  "6xl": "max-w-full md:max-w-6xl",
  "7xl": "max-w-full md:max-w-7xl",
  full: "max-w-full mx-0 md:mx-4",
};

/**
 * Modal — Radix Dialog with portal so it renders above all stacking contexts.
 * Backdrop blur + scale animation matches design-system spec.
 */
export function Modal({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true,
  isFullscreen = false,
  size = "lg",
  closeOnOverlayClick = true,
  closeOnEscape = true,
  "aria-label": ariaLabel,
}: ModalProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  // Track this modal in the global stack so nested modals can hide their
  // ancestors. When `isStacked` is true, this modal stays mounted (preserving
  // state) but its overlay + content are visually hidden so the topmost modal
  // is the only one the user sees — backdrops do NOT layer.
  const stackId = useId();
  const { isStacked } = useModalStack(stackId, isOpen);

  if (import.meta.env.DEV && isOpen && !ariaLabel) {
    console.warn(
      "[Modal] Missing `aria-label` — falling back to generic 'Dialog'. " +
        "Pass a meaningful aria-label so screen readers can announce the modal."
    );
  }
  const accessibleTitle = ariaLabel || "Dialog";

  const contentClass = isFullscreen
    ? "w-full h-full"
    : cn(
        "relative w-full max-h-[calc(100vh-1rem)] md:max-h-[calc(100vh-2rem)] flex flex-col",
        "rounded-2xl md:rounded-3xl bg-[var(--card)] shadow-[var(--shadow-lg)]",
        modalSizes[size]
      );

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        {/* Overlay — hidden when this modal is stacked behind a nested one so
            backdrops don't layer (parent modal stays mounted but invisible).
            Backdrop: spec oklch(0.18 0.02 240 / 0.4) cool slate at 40% opacity.
            fadeOverlay keyframe handles entrance; Tailwind animate-out covers close. */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[var(--z-overlay-elevated)] bg-[var(--modal-backdrop)] backdrop-blur-sm",
            "data-[state=open]:animate-[fadeOverlay_200ms_ease-out]",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            isStacked && "invisible"
          )}
          onClick={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
        />
        {/* Content wrapper — also hidden when stacked. `invisible + pointer-events-none`
            preserves React state and Radix focus trap targets while removing
            visual + interaction surface. */}
        <div
          className={cn(
            "fixed inset-0 z-[var(--z-modal-elevated)] flex items-center justify-center overflow-y-auto p-2 md:p-4",
            isStacked && "pointer-events-none invisible"
          )}
        >
          <DialogPrimitive.Content
            onEscapeKeyDown={closeOnEscape ? undefined : (e) => e.preventDefault()}
            className={cn(contentClass, className)}
            aria-describedby={undefined}
          >
            {/* sr-only DialogTitle satisfies Radix a11y requirement for every
                Modal — visible titles inside ModalHeader remain unaffected. */}
            <DialogPrimitive.Title className="sr-only">
              {accessibleTitle}
            </DialogPrimitive.Title>
            {showCloseButton && (
              <DialogPrimitive.Close
                className={cn(
                  "absolute right-3 top-3 z-10 flex h-10 w-10 md:h-9 md:w-9",
                  "items-center justify-center rounded-full",
                  "bg-[var(--muted)] text-[var(--muted-foreground)]",
                  "transition-colors hover:bg-[var(--accent)] hover:text-[var(--foreground)]",
                  "focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus-ring)]"
                )}
                aria-label="Close"
              >
                <X className="size-4" />
              </DialogPrimitive.Close>
            )}
            {children}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** ModalHeader — top section with border-bottom */
export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-4 py-3 md:px-6 md:py-4 border-b border-[var(--border)] shrink-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
ModalHeader.displayName = "ModalHeader";

/** ModalBody — scrollable content area */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("p-4 md:p-6 min-h-0 overflow-y-auto", className)}
      {...props}
    >
      {children}
    </div>
  )
);
ModalBody.displayName = "ModalBody";

/** ModalFooter — bottom section with border-top */
export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-4 py-3 md:px-6 md:py-4 border-t border-[var(--border)]",
        "flex flex-col-reverse gap-2 md:flex-row md:justify-end md:gap-3 shrink-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
ModalFooter.displayName = "ModalFooter";

/** ModalClose — alias to Radix close trigger, for composability */
export const ModalClose = DialogPrimitive.Close;
